import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import ConfirmButton from "@/components/ConfirmButton";
import CompanyForm from "@/pages/companies/CompanyForm";

export default function Companies() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("empresa"); // "empresa" | "cnpj"
  const [cnpjBuckets, setCnpjBuckets] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/companies");
    setItems(data);
  }, []);
  const loadCnpj = useCallback(async () => {
    try {
      const { data } = await api.get("/cnpj/index");
      setCnpjBuckets(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "cnpj") loadCnpj(); }, [view, loadCnpj]);

  const openNew = () => { setEditing(null); setShow(true); };
  const openEdit = (c) => { setEditing(c); setShow(true); };

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/companies/${editing.id}`, form);
        toast.success("Empresa atualizada");
      } else {
        await api.post("/companies", form);
        toast.success("Empresa criada");
      }
      setShow(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Empresa excluída");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-10">
        <div>
          <div className="overline mb-2">Cadastro</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Empresas</h1>
          <p className="text-slate-600 mt-2">Gerencie as empresas avaliadas em sua carteira.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex border border-slate-300 bg-white p-1" data-testid="companies-view-toggle">
            <button onClick={() => setView("empresa")}
                    data-testid="companies-view-empresa"
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                      view === "empresa" ? "bg-emerald-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              Por empresa
            </button>
            <button onClick={() => setView("cnpj")}
                    data-testid="companies-view-cnpj"
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                      view === "cnpj" ? "bg-emerald-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              Por CNPJ
            </button>
          </div>
          <Link to="/app/importar/pgr"
                className="btn btn-ghost border border-emerald-300 text-emerald-900 hover:bg-emerald-50"
                data-testid="company-import-pgr-button">
            <i className="fa-solid fa-wand-magic-sparkles"></i> Importar PGR
          </Link>
          <button onClick={openNew} className="btn btn-primary" data-testid="company-new-button">
            <i className="fa-solid fa-plus"></i> Nova empresa
          </button>
        </div>
      </div>

      <div className="card">
        {view === "cnpj" ? (
          cnpjBuckets.length === 0 ? (
            <div className="text-slate-500 py-12 text-center" data-testid="cnpj-empty">
              <i className="fa-solid fa-id-card text-4xl mb-4 text-slate-300"></i>
              <p>Nenhuma empresa com CNPJ informado.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="cnpj-table">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-3 font-semibold uppercase text-xs tracking-wider">CNPJ</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Razão Social</th>
                  <th className="font-semibold uppercase text-xs tracking-wider text-center">Cadastros</th>
                  <th className="font-semibold uppercase text-xs tracking-wider text-center">Avaliações</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cnpjBuckets.map((b) => (
                  <tr key={b.cnpj} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`cnpj-row-${b.cnpj}`}>
                    <td className="py-3 font-mono text-xs">{b.cnpj_formatted || b.cnpj}</td>
                    <td className="font-semibold text-slate-900">{b.razao_social}</td>
                    <td className="text-center font-mono">{b.company_count}</td>
                    <td className="text-center font-mono">{b.assessment_count}</td>
                    <td className="text-right">
                      <Link to={`/app/cnpj/${b.cnpj}/consolidado`}
                            className="btn btn-secondary text-xs"
                            data-testid={`cnpj-view-${b.cnpj}`}>
                        <i className="fa-solid fa-layer-group"></i> Ver consolidado
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : items.length === 0 ? (
          <div className="text-slate-500 py-12 text-center">
            <i className="fa-solid fa-building text-4xl mb-4 text-slate-300"></i>
            <p>Nenhuma empresa cadastrada.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 font-semibold uppercase text-xs tracking-wider">Empresa</th>
                <th className="font-semibold uppercase text-xs tracking-wider">CNPJ</th>
                <th className="font-semibold uppercase text-xs tracking-wider">CNAE</th>
                <th className="font-semibold uppercase text-xs tracking-wider">Grau</th>
                <th className="font-semibold uppercase text-xs tracking-wider">Cidade/UF</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-slate-100 hover:bg-emerald-50/40 cursor-pointer transition-colors"
                  data-testid={`company-row-${c.id}`}
                  onClick={(e) => {
                    // Evita navegar se o clique foi em algum botão da linha
                    if (e.target.closest("button, a, [data-stop]")) return;
                    nav(`/app/empresas/${c.id}`);
                  }}
                >
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      {c.logo_base64 ? (
                        <img src={c.logo_base64} alt="" className="w-10 h-10 object-contain border border-slate-200 bg-white" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 flex items-center justify-center text-slate-300">
                          <i className="fa-solid fa-building text-sm"></i>
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-slate-900 group-hover:text-emerald-900">{c.razao_social}</div>
                        {c.nome_fantasia && <div className="text-xs text-slate-500">{c.nome_fantasia}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="text-slate-600 font-mono text-xs">{c.cnpj || "—"}</td>
                  <td className="text-slate-600 font-mono text-xs">{c.cnae || "—"}</td>
                  <td className="text-slate-600">{c.grau_risco || "—"}</td>
                  <td className="text-slate-600 text-xs">
                    {c.cidade ? `${c.cidade}${c.uf ? "/" + c.uf : ""}` : "—"}
                  </td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <Link
                      to={`/app/empresas/${c.id}`}
                      className="btn btn-primary text-xs"
                      data-testid={`company-open-${c.id}`}
                      title="Abrir Prontuário Corporativo SST"
                    >
                      <i className="fa-solid fa-arrow-right"></i> Abrir Empresa
                    </Link>
                    <button onClick={() => openEdit(c)} className="btn btn-ghost text-xs" data-testid={`company-edit-${c.id}`}>
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <ConfirmButton
                      onConfirm={() => del(c.id)}
                      testid={`company-delete-${c.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {show && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4"
             onClick={() => setShow(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl">
            <CompanyForm
              initial={editing}
              onCancel={() => setShow(false)}
              onSubmit={save}
            />
          </div>
        </div>
      )}
    </div>
  );
}
